import {useState, useRef, useEffect} from "react";
// @ts-ignore
import style from './Map.module.css';
import {useGesture} from "@use-gesture/react";
import {clamp} from "./utils";
import {RawSystem, SelectedSystem, Sector, CustomMarker, SelectedFaction} from "./types";
import { PlusCircle as IconCirclePlus, MinusCircle as IconCircleMinus } from "@phosphor-icons/react";

const mapWidth = 1000;
const MAP_IMAGE_URL = 'https://assets.swc-forge.com/map-full.png';

interface Position {
    x: number,
    y: number,
}

interface GalaxyMapProps {
    systems: RawSystem[],
    selected: SelectedSystem[],
    showLabels: boolean,
    sectors: Sector[],
    customMarkers: CustomMarker[],
    selectedFactions: SelectedFaction[],
    mode: 'standard' | 'political',
    backgroundColor: string,
    hideNonHighlighted: boolean,
}

const OPACITY = 80;

export default function GalaxyMap({
    systems,
    selected,
    showLabels,
    sectors,
    customMarkers,
    selectedFactions,
    mode,
    backgroundColor,
    hideNonHighlighted
}: GalaxyMapProps) {
    const [zoom, setZoom] = useState<number>(1);
    const positionRef = useRef<Position>({x: 0, y: 0});
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [dimensions, setDimensions] = useState({width: 0, height: 0});
    const [backgroundImage, setBackgroundImage] = useState<HTMLImageElement | null>(null);

    // Load background image
    useEffect(() => {
        const img = new Image();
        img.src = MAP_IMAGE_URL;
        img.onload = () => {
            setBackgroundImage(img);
        };
    }, []);


    // Transform galaxy coordinates to canvas coordinates
    const toCanvasCoords = (x: number, y: number, currentZoom: number = zoom) => {
        return {
            x: (x + 500) * currentZoom,
            y: (-y + 500) * currentZoom
        };
    };

    // Calculate luminance of a color and return black or white text color
    const getContrastTextColor = (hexColor: string): string => {
        // Convert hex to RGB
        const hex = hexColor.replace('#', '');
        const r = parseInt(hex.substr(0, 2), 16);
        const g = parseInt(hex.substr(2, 2), 16);
        const b = parseInt(hex.substr(4, 2), 16);

        // Calculate relative luminance (WCAG formula)
        const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        // Return white for dark backgrounds, black for light backgrounds
        return luminance > 0.5 ? '#000000' : '#ffffff';
    };

    // Draw the map on canvas
    const drawMap = (ctx: CanvasRenderingContext2D, currentZoom: number) => {
        const canvas = ctx.canvas;

        // Fill with background color
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw background image
        if (backgroundImage) {
            ctx.save();
            ctx.globalAlpha = 0.5;
            // ctx.drawImage(backgroundImage, 0, 0, mapWidth * currentZoom, mapWidth * currentZoom);
            ctx.restore();
        }

        // Pre-calculate sector control status for selected factions
        const sectorControlStatus = new Map<string, { factionId: string, fullyControlled: boolean }>();
        if (mode === 'political' && selectedFactions.length > 0) {
            sectors.forEach(sector => {
                const controllingFaction = selectedFactions.find(f => f.id === sector.government);
                if (controllingFaction) {
                    // Get all systems in this sector
                    const systemsInSector = systems.filter(sys => sys.sector === sector.id);

                    // Check if ALL systems are controlled by the same faction
                    const fullyControlled = systemsInSector.length > 0 &&
                        systemsInSector.every(sys => sys.government === controllingFaction.id);

                    sectorControlStatus.set(sector.id, {
                        factionId: controllingFaction.id,
                        fullyControlled
                    });
                }
            });
        }

        // Draw sector boundaries
        if (sectors.length > 0) {
            sectors.forEach(sector => {
                if (sector.vertices && sector.vertices.length > 0) {
                    ctx.beginPath();
                    const firstPoint = toCanvasCoords(sector.vertices[0][0], sector.vertices[0][1], currentZoom);
                    ctx.moveTo(firstPoint.x, firstPoint.y);

                    for (let i = 1; i < sector.vertices.length; i++) {
                        const point = toCanvasCoords(sector.vertices[i][0], sector.vertices[i][1], currentZoom);
                        ctx.lineTo(point.x, point.y);
                    }
                    ctx.closePath();

                    // Check if this sector should be highlighted in political mode
                    const controlStatus = sectorControlStatus.get(sector.id);

                    if (controlStatus) {
                        const faction = selectedFactions.find(f => f.id === controlStatus.factionId);
                        if (faction) {
                            if (controlStatus.fullyControlled) {
                                // Solid fill for fully controlled sectors
                                ctx.fillStyle = faction.color + OPACITY;
                                ctx.fill();
                            } else {
                                // Striped pattern for partially controlled sectors
                                ctx.save();

                                // Create stripe pattern
                                const stripeCanvas = document.createElement('canvas');
                                stripeCanvas.width = 10;
                                stripeCanvas.height = 10;
                                const stripeCtx = stripeCanvas.getContext('2d');
                                if (stripeCtx) {
                                    // Background
                                    stripeCtx.fillStyle = faction.color + '20';
                                    stripeCtx.fillRect(0, 0, 10, 10);
                                    // Diagonal stripes
                                    stripeCtx.strokeStyle = faction.color + '60';
                                    stripeCtx.lineWidth = 2;
                                    stripeCtx.beginPath();
                                    stripeCtx.moveTo(0, 10);
                                    stripeCtx.lineTo(10, 0);
                                    stripeCtx.stroke();
                                    stripeCtx.beginPath();
                                    stripeCtx.moveTo(-5, 5);
                                    stripeCtx.lineTo(5, -5);
                                    stripeCtx.stroke();
                                    stripeCtx.beginPath();
                                    stripeCtx.moveTo(5, 15);
                                    stripeCtx.lineTo(15, 5);
                                    stripeCtx.stroke();
                                }

                                const pattern = ctx.createPattern(stripeCanvas, 'repeat');
                                if (pattern) {
                                    ctx.fillStyle = pattern;
                                    ctx.fill();
                                }
                                ctx.restore();
                            }
                            ctx.strokeStyle = faction.color;
                            ctx.lineWidth = 2;
                        }
                    } else {
                        ctx.strokeStyle = '#888888';
                        ctx.lineWidth = 1;
                    }
                    ctx.stroke();
                }
            });
        }

        // Draw all systems (gray)
        systems.forEach(sys => {
            const pos = toCanvasCoords(sys.galX, sys.galY, currentZoom);

            // Check if system should be highlighted in political mode
            // Only highlight if: system is controlled by selected faction
            // AND the sector is NOT controlled by the same faction (regardless of full/partial control)
            const sectorControl = sectorControlStatus.get(sys.sector);
            const systemControllingFaction = selectedFactions.find(f => f.id === sys.government);

            const shouldHighlightSystem = mode === 'political'
                && systemControllingFaction
                && !(sectorControl && sectorControl.factionId === sys.government);

            if (shouldHighlightSystem && systemControllingFaction) {
                ctx.fillStyle = systemControllingFaction.color;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
                ctx.fill();
                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1;
                ctx.stroke();
            } else if (!selected.some(s => s.id === sys.id) && !hideNonHighlighted) {
                // Only draw gray systems if not selected and not in political highlight
                // AND hideNonHighlighted is false
                ctx.fillStyle = '#a2a2a280';
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 2.5, 0, Math.PI * 2);
                ctx.fill();
            }
        });

        // Draw selected systems (in standard mode)
        if (mode === 'standard') {
            selected.forEach(sys => {
                const pos = toCanvasCoords(sys.galX, sys.galY, currentZoom);

                ctx.fillStyle = sys.color;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
                ctx.fill();

                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
                ctx.stroke();

                // Draw label if enabled
                if (showLabels) {
                    ctx.save();
                    ctx.fillStyle = sys.color;
                    ctx.font = '12px sans-serif';
                    const textWidth = ctx.measureText(sys.name).width;
                    const padding = 2;

                    // Background for label
                    ctx.fillRect(
                        pos.x - textWidth / 2 - padding,
                        pos.y + 6,
                        textWidth + padding * 2,
                        14
                    );

                    // Text with contrast color
                    ctx.fillStyle = getContrastTextColor(sys.color);
                    ctx.textAlign = 'center';
                    ctx.fillText(sys.name, pos.x, pos.y + 18);
                    ctx.restore();
                }
            });

            // Draw custom markers (identical to systems)
            customMarkers.forEach(marker => {
                const pos = toCanvasCoords(marker.x, marker.y, currentZoom);

                // Draw marker as circle (same as selected systems)
                ctx.fillStyle = marker.color;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
                ctx.fill();

                ctx.strokeStyle = 'white';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.arc(pos.x, pos.y, 4, 0, Math.PI * 2);
                ctx.stroke();

                // Draw label (always show for custom markers)
                if (showLabels) {
                    ctx.save();
                    ctx.fillStyle = marker.color;
                    ctx.font = '12px sans-serif';
                    const textWidth = ctx.measureText(marker.label).width;
                    const padding = 2;

                    // Background for label
                    ctx.fillRect(
                        pos.x - textWidth / 2 - padding,
                        pos.y + 6,
                        textWidth + padding * 2,
                        14
                    );

                    // Text with contrast color
                    ctx.fillStyle = getContrastTextColor(marker.color);
                    ctx.textAlign = 'center';
                    ctx.fillText(marker.label, pos.x, pos.y + 18);
                    ctx.restore();
                }
            });
        }
    };

    // Redraw canvas when dependencies change
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        drawMap(ctx, zoom);
    }, [backgroundImage, systems, selected, showLabels, zoom, sectors, customMarkers, selectedFactions, mode, backgroundColor, hideNonHighlighted]);

    const updateBounds = (newZoom: number) => {
        const maxX = dimensions.width - mapWidth * newZoom;
        const maxY = dimensions.height - mapWidth * newZoom;
        return {maxX, maxY};
    };

    const updatePosition = (x: number, y: number) => {
        const bounds = updateBounds(zoom);
        const xPos = clamp(x, bounds.maxX, 0);
        const yPos = clamp(y, bounds.maxY, 0);
        positionRef.current = {x: xPos, y: yPos};
        if(canvasRef.current) {
            canvasRef.current.style.transform = `translate(${xPos}px, ${yPos}px)`;
        }
    };

    useEffect(() => {
        const maxX = dimensions.width - mapWidth * zoom;
        const maxY = dimensions.height - mapWidth * zoom;
        const xPos = clamp(positionRef.current.x, maxX, 0);
        const yPos = clamp(positionRef.current.y, maxY, 0);
        positionRef.current = {x: xPos, y: yPos};
        if (canvasRef.current) {
            canvasRef.current.style.transform = `translate(${xPos}px, ${yPos}px)`;
        }
    }, [dimensions.width, dimensions.height, zoom]);

    const bind = useGesture(
        {
            onDrag: ({offset: [ox, oy]}) => {
                updatePosition(ox, oy);
            },
            onPinch: ({offset: [s], origin: [ox, oy]}) => {
                const newZoom = clamp(s / 200, 0.4, 4);
                const bounds = updateBounds(newZoom);

                const currentX = positionRef.current.x;
                const currentY = positionRef.current.y;

                const newOffsetX = ox - (ox - currentX) * (newZoom / zoom);
                const newOffsetY = oy - (oy - currentY) * (newZoom / zoom);

                const xPos = clamp(newOffsetX, bounds.maxX, 0);
                const yPos = clamp(newOffsetY, bounds.maxY, 0);

                setZoom(newZoom);
                positionRef.current = {x: xPos, y: yPos};
            },
        },
        {
            drag: {from: () => [positionRef.current.x, positionRef.current.y]},
            pinch: {scaleBounds: {min: 1, max: 4}},
        }
    );

    useEffect(() => {
        if (!containerRef.current) return;
        const container = containerRef.current;

        const updateDimensions = () => {
            setDimensions({
                width: container.clientWidth,
                height: container.clientHeight,
            });
        };

        updateDimensions();

        const observer = new ResizeObserver(updateDimensions);
        observer.observe(container);

        return () => observer.disconnect();
    }, []);

    function setZoomAndUpdateBounds(newZoom: number) {
        const clampedZoom = clamp(newZoom, 0.4, 4);
        setZoom(clampedZoom);
        let px = positionRef.current.x + (zoom - clampedZoom) * dimensions.width / 2;
        let py = positionRef.current.y + (zoom - clampedZoom) * dimensions.height / 2;
        updatePosition(px, py);
    }

    // Resize canvas when dimensions change
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        canvas.width = mapWidth * zoom;
        canvas.height = mapWidth * zoom;

        const ctx = canvas.getContext('2d');
        if (ctx) {
            drawMap(ctx, zoom);
        }
    }, [dimensions, zoom]);

    return (
        <div ref={containerRef} className={style.wrapper}>
            <div className={style.inner}>
                <div className={style.button} onClick={() => setZoomAndUpdateBounds(zoom + 0.4)}><IconCirclePlus size={24} /></div>
                <div className={style.button} onClick={() => setZoomAndUpdateBounds(zoom - 0.4)}><IconCircleMinus size={24} /></div>

                <div className={style.content} {...bind()}>
                    <canvas
                        ref={canvasRef}
                        className={style.canvas}
                        width={mapWidth * zoom}
                        height={mapWidth * zoom}
                    />
                </div>
            </div>
        </div>
    );
}
